import Layout from "@/components/Layout";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { EmployeeLoanCreateDialog } from "@/components/hr/EmployeeLoanCreateDialog";
import { SalaryAdvanceCreateDialog } from "@/components/hr/SalaryAdvanceCreateDialog";
import { SalaryIncrementCreateDialog } from "@/components/hr/SalaryIncrementCreateDialog";
import { AttendanceAdjustmentDialog } from "@/components/hr/AttendanceAdjustmentDialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { deleteEmployee, employeeApiErrorMessage, getEmployeeById, syncEmployeesFromApi, syncSingleEmployeeFromApi } from "@/lib/hr/employeesStore";
import { listLeaveRequestsForEmployee, syncLeaveRequestsFromApi } from "@/lib/hr/leaveRequestsStore";
import type { EmployeeAllowance, EmployeeDeduction, EmployeeRecord } from "@/lib/hr/employeesStore";
import { listLeavePolicies, syncLeavePoliciesFromApi } from "@/lib/hr/leavePoliciesStore";
import { listLeaveYears, syncLeaveYearsFromApi } from "@/lib/hr/leaveYearsStore";
import { listSalaryIncrements, type SalaryIncrementRecord } from "@/lib/hr/salaryIncrementsStore";
import { listEmployeeLoans, type EmployeeLoanRecord } from "@/lib/hr/loansStore";
import { listSalaries, downloadSalaryPdf, type SalaryRecord } from "@/lib/hr/salaryProcessStore";
import { fetchAttendanceRecords, type AttendanceRecordRow } from "@/lib/hr/attendanceStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatTimeDisplayAmPm } from "@/components/hr/TimePickerField";
import { LoanIcon } from "@/components/icons/LoanIcon";
import {
  ArrowLeft,
  Banknote,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  ClipboardList,
  CreditCard,
  Download,
  Edit,
  Eye,
  ExternalLink,
  FileCheck2,
  FileText,
  HandCoins,
  Home,
  Mail,
  MapPin,
  Phone,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  TrendingUp,
  User,
  UserCircle2,
  Users,
  Wallet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { format, isValid, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function formatSlipDayPayTitle(title: string): string {
  const match = title.match(/^(.+) \((\d+) days? @ ([\d.]+) × (-?[\d.]+)\)$/);
  if (!match) return title;
  const days = Number(match[2]);
  const effective = Math.round(Number(match[3]) * Number(match[4]) * 100) / 100;
  return `${match[1]} (${days} day${days === 1 ? "" : "s"} @ ${effective.toFixed(2)})`;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function initialsFromName(name: string | null | undefined): string {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function parseMoney(raw: string | number | null | undefined): number {
  const t = String(raw ?? "").replace(/[^\d.]/g, "");
  const n = Number(t);
  return Number.isFinite(n) ? n : 0;
}

function formatLongDate(d: string | null | undefined): string {
  const t = String(d ?? "").trim().slice(0, 10);
  if (!t) return "—";
  const dt = new Date(`${t}T00:00:00`);
  if (!Number.isFinite(dt.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(dt);
}

function formatPkMoney(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return formatCurrency(n);
}

function formatStreetAddress(e: EmployeeRecord, kind: "present" | "permanent"): string {
  if (kind === "present") {
    const lines = [e.presentAddressLine1, e.presentAddressLine2].map((x) => String(x ?? "").trim()).filter(Boolean);
    const region = [e.presentCity, e.presentState, e.presentCountry].map((x) => String(x ?? "").trim()).filter(Boolean).join(", ");
    const block = lines.length ? lines.join(", ") : "";
    if (block && region) return `${block}, ${region}`;
    return block || region || "—";
  }
  const lines = [e.permanentAddressLine1, e.permanentAddressLine2].map((x) => String(x ?? "").trim()).filter(Boolean);
  const region = [e.permanentCity, e.permanentState, e.permanentCountry].map((x) => String(x ?? "").trim()).filter(Boolean).join(", ");
  const block = lines.length ? lines.join(", ") : "";
  if (block && region) return `${block}, ${region}`;
  return block || region || "—";
}

function SectionHeader({
  title,
  subtitle,
  icon: Icon,
  action,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-200">
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="w-1.5 h-5 bg-indigo-500 rounded-full shrink-0 mt-0.5" />
        {Icon ? <Icon className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" /> : null}
        <div className="min-w-0">
          <h2 className="font-bold text-slate-900 text-base tracking-tight">{title}</h2>
          {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number | null | undefined;
  highlight?: boolean;
}) {
  const display = String(value ?? "").trim() || "—";
  return (
    <div
      className={cn(
        "flex gap-3 py-2.5 border-b border-slate-100 last:border-0",
        highlight && "bg-amber-50/60 p-2.5 rounded-lg border-amber-200 border my-1",
      )}
    >
      <div className="shrink-0 mt-0.5 text-slate-400">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
        <p className={cn("text-sm mt-0.5 break-words", highlight ? "font-bold text-amber-900" : "font-semibold text-slate-800")}>
          {display}
        </p>
      </div>
    </div>
  );
}

function SalaryLineItem({ prefix, label, amount }: { prefix?: string; label: string; amount: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm py-1.5">
      <span className="text-slate-700 font-medium min-w-0">
        {prefix ? <span className="text-slate-400 mr-1">{prefix}</span> : null}
        {label}
      </span>
      <span className="font-semibold text-slate-900 tabular-nums shrink-0">{formatPkMoney(amount)}</span>
    </div>
  );
}

function SalarySubtotalRow({ label, amount, emphasize }: { label: string; amount: number; emphasize?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4 pt-2.5 mt-2 border-t border-slate-200",
        emphasize && "font-bold",
      )}
    >
      <span className={cn("text-sm", emphasize ? "text-slate-900 font-bold" : "text-slate-700 font-medium")}>{label}</span>
      <span className={cn("tabular-nums shrink-0", emphasize ? "text-lg font-bold text-indigo-600" : "text-sm font-bold text-slate-900")}>
        {formatPkMoney(amount)}
      </span>
    </div>
  );
}

export default function EmployeeView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leaveVersion, setLeaveVersion] = useState(0);
  const [refVersion, setRefVersion] = useState(0);
  const [employeeTick, setEmployeeTick] = useState(0);

  // Dialog states
  const [incrementOpen, setIncrementOpen] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);

  // Navigation segment state
  const [activeTab, setActiveTab] = useState<"overview" | "basic" | "policies" | "attendance" | "financial">("overview");

  // Dynamic modules state
  const [salaryIncrements, setSalaryIncrements] = useState<SalaryIncrementRecord[]>([]);
  const [employeeLoans, setEmployeeLoans] = useState<EmployeeLoanRecord[]>([]);
  const [payslips, setPayslips] = useState<SalaryRecord[]>([]);
  const [selectedPayslip, setSelectedPayslip] = useState<SalaryRecord | null>(null);
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceRecordRow[]>([]);
  const [selectedLog, setSelectedLog] = useState<AttendanceRecordRow | null>(null);

  const user = useMemo(() => getStoredAuthUser(), []);
  const canView = can(user, "employee_can_view");
  const canEdit = can(user, "employee_can_update");
  const canDelete = can(user, "employee_can_delete");
  const canCreateSalaryIncrements = can(user, "salary_increment_can_create");
  const canCreateEmployeeLoans = can(user, "employee_loan_can_create");
  const canCreateSalaryAdvances = can(user, "salary_advance_can_create");

  const [searchParams] = useSearchParams();
  const isDebug = searchParams.get("debug") === "true";
  const [syncError, setSyncError] = useState<string | null>(null);

  const employee = useMemo(() => (id ? getEmployeeById(id) : undefined), [id, employeeTick]);

  useEffect(() => {
    if (!canView) {
      toast.error("User does not have the right permissions.");
      navigate("/dashboard", { replace: true });
    }
  }, [canView, navigate]);

  useEffect(() => {
    if (!id) return;
    void syncLeaveRequestsFromApi({ employee_id: id })
      .then(() => setLeaveVersion((v) => v + 1))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    void Promise.all([syncLeaveYearsFromApi(), syncLeavePoliciesFromApi()])
      .then(() => setRefVersion((v) => v + 1))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setSyncError(null);
    void syncSingleEmployeeFromApi(id)
      .then(() => setEmployeeTick((v) => v + 1))
      .catch((err) => {
        console.error("SYNC SINGLE EMPLOYEE ERROR:", err);
        setSyncError(err instanceof Error ? err.message : String(err));
      });
  }, [id]);

  // Fetch increments, loans, payslips, and attendance logs for employee
  useEffect(() => {
    if (!id) return;
    void listSalaryIncrements({ employeeId: id, perPage: 20 })
      .then((res) => setSalaryIncrements(res.rows))
      .catch(() => {});

    void listEmployeeLoans({ employeeId: id, perPage: 20 })
      .then((res) => setEmployeeLoans(res.rows))
      .catch(() => {});

    void listSalaries({ employeeId: id, perPage: 20 })
      .then((res) => {
        setPayslips(res.rows.filter((s) => s.status === "finalized" || s.status === "paid"));
      })
      .catch(() => {});

    void fetchAttendanceRecords({ employeeId: id, perPage: 7 })
      .then((res) => {
        setAttendanceLogs(res.rows);
        if (res.rows.length > 0) setSelectedLog(res.rows[0]);
      })
      .catch(() => {});
  }, [id, employeeTick]);

  const attendanceStats = useMemo(() => {
    if (!attendanceLogs || attendanceLogs.length === 0) {
      return {
        presentCount: 0,
        attendanceRate: 0,
        lateCount: 0,
        lateText: "Within grace limits",
        leavesCount: 0,
        leavesText: "None recorded",
        totalOvertimeHrs: 0,
        overtimeText: "No overtime logged",
      };
    }

    let present = 0;
    let late = 0;
    let leavesCount = 0;
    let totalOtMinutes = 0;
    let totalDays = attendanceLogs.length;

    attendanceLogs.forEach((row) => {
      const statusLower = (row.status || "").toLowerCase();
      if (
        statusLower.includes("present") ||
        statusLower.includes("worked") ||
        statusLower.includes("late") ||
        row.checkIn
      ) {
        present++;
      }

      if (row.isLate || statusLower.includes("late") || (row.lateMinutes || 0) > 0) {
        late++;
      }

      if (statusLower.includes("leave")) {
        leavesCount++;
      }

      totalOtMinutes += (row.overtimeMinutes || 0) + (row.offDayOvertimeMinutes || 0);
    });

    const rate = totalDays > 0 ? Math.round((present / totalDays) * 100) : 0;
    const overtimeHrs = Math.round((totalOtMinutes / 60) * 10) / 10;

    const totalLateMinutes = attendanceLogs.reduce((acc, row) => acc + (row.lateMinutes || 0), 0);
    const lateText = totalLateMinutes > 0 ? `${totalLateMinutes}m total delay` : "Within grace limits";
    const leavesText = leavesCount > 0 ? `${leavesCount} day${leavesCount === 1 ? "" : "s"} approved` : "No leaves in period";
    const overtimeText = overtimeHrs > 0 ? `${overtimeHrs}h logged at 1.5x` : "0h overtime recorded";

    return {
      presentCount: present,
      attendanceRate: rate,
      lateCount: late,
      lateText,
      leavesCount: leavesCount,
      leavesText,
      totalOvertimeHrs: overtimeHrs,
      overtimeText,
    };
  }, [attendanceLogs]);

  const leaves = useMemo(() => (id ? listLeaveRequestsForEmployee(id) : []), [id, leaveVersion]);
  const leaveYears = useMemo(() => listLeaveYears(), [refVersion]);
  const leavePolicies = useMemo(() => listLeavePolicies(), [refVersion]);

  const leaveYearTabs = useMemo(() => {
    const yearsFromAssignments =
      employee?.leavePolicyAssignments?.map((a) => ({
        id: a.leaveYearId,
        label: a.leaveYearLabel,
        startDate: a.leaveYearStartDate,
        endDate: a.leaveYearEndDate,
        active: a.leaveYearActive,
      })) ?? [];

    const yearsFromAllocations =
      employee?.leaveAllocations?.map((a) => ({
        id: a.leaveYearId,
        label: a.leaveYearLabel,
        startDate: a.leaveYearStartDate,
        endDate: a.leaveYearEndDate,
        active: a.leaveYearActive,
      })) ?? [];

    const byId = new Map<string, { id: string; label: string; startDate: string; endDate: string; active: boolean }>();
    for (const y of [...yearsFromAssignments, ...yearsFromAllocations]) {
      if (!y.id) continue;
      if (!byId.has(y.id)) byId.set(y.id, y);
      else if (y.active) byId.set(y.id, y);
    }

    const merged = Array.from(byId.values());
    merged.sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1;
      const ad = a.startDate || "";
      const bd = b.startDate || "";
      return bd.localeCompare(ad) || b.id.localeCompare(a.id);
    });

    if (merged.length === 0 && employee?.leaveYearId?.trim()) {
      const yearId = employee.leaveYearId.trim();
      const y = leaveYears.find((x) => String(x.id) === yearId);
      merged.push({
        id: yearId,
        label: y?.label ?? yearId,
        startDate: y?.startDate ?? "",
        endDate: y?.endDate ?? "",
        active: Boolean(y?.active),
      });
    }

    return merged;
  }, [employee?.leavePolicyAssignments, employee?.leaveAllocations, employee?.leaveYearId, leaveYears]);

  const defaultLeaveTab = useMemo(() => {
    const active = leaveYearTabs.find((y) => y.active);
    return active?.id || leaveYearTabs[0]?.id || "leave-year";
  }, [leaveYearTabs]);

  const allowancesTotal = useMemo(() => {
    if (!employee?.allowances?.length) return 0;
    return employee.allowances.reduce((sum, a) => sum + parseMoney(a.amount), 0);
  }, [employee]);

  const deductionsTotal = useMemo(() => {
    if (!employee?.deductions?.length) return 0;
    return employee.deductions.reduce((sum, d) => sum + parseMoney(d.amount), 0);
  }, [employee]);

  const workShiftCurrent = useMemo(() => {
    const list = employee?.workShiftAssignments ?? [];
    if (list.length === 0) return null;
    const currentByOpenEnd = list.find((a) => !a.effectiveTo) ?? null;
    if (currentByOpenEnd) return currentByOpenEnd;
    const id = (employee?.workShiftId ?? "").trim();
    if (!id) return list[0] ?? null;
    return list.find((a) => String(a.workShiftId) === id) ?? (list[0] ?? null);
  }, [employee?.workShiftAssignments, employee?.workShiftId]);

  const formattedOffDays = useMemo(() => {
    const days = employee?.employeeOffDays ?? [];
    if (days.length === 0) return "—";
    const mapping: Record<string, string> = {
      mon: "Monday",
      tue: "Tuesday",
      wed: "Wednesday",
      thu: "Thursday",
      fri: "Friday",
      sat: "Saturday",
      sun: "Sunday",
    };
    return days.map((d) => mapping[d] || d).join(", ");
  }, [employee?.employeeOffDays]);

  const graceMinutes = useMemo(() => {
    const rule = employee?.workPolicyRules?.find(r => r.ruleKey === "coming_late_grace_minutes");
    if (rule) {
      return `${rule.ruleValue} Mins`;
    }
    return "10 Mins";
  }, [employee?.workPolicyRules]);

  const overtimePolicy = useMemo(() => {
    const enabledRule = employee?.salaryPolicyRules?.find(r => r.ruleKey === "overtime_tracking_enabled");
    const isEnabled = enabledRule ? (enabledRule.ruleValue === "1" || enabledRule.ruleValue === "true" || enabledRule.ruleValue === "yes") : true;
    if (!isEnabled) {
      return "Not Allowed";
    }

    const splitRule = employee?.workPolicyRules?.find(r => r.ruleKey === "enable_split_ot");
    const isSplit = splitRule ? (splitRule.ruleValue === "1" || splitRule.ruleValue === "true" || splitRule.ruleValue === "yes") : false;

    if (isSplit) {
      const startRule = employee?.salaryPolicyRules?.find(r => r.ruleKey === "shift_start_ot_multiplier");
      const endRule = employee?.salaryPolicyRules?.find(r => r.ruleKey === "shift_end_ot_multiplier");
      const start = startRule ? startRule.ruleValue : "1.5";
      const end = endRule ? endRule.ruleValue : "1.5";
      return `Allowed (Start: ${start}x, End: ${end}x)`;
    } else {
      const multiplierRule = employee?.salaryPolicyRules?.find(r => r.ruleKey === "overtime_rate_multiplier");
      const multiplier = multiplierRule ? multiplierRule.ruleValue : "1.5";
      return `Allowed (${multiplier}x Rate)`;
    }
  }, [employee?.workPolicyRules, employee?.salaryPolicyRules]);

  const attendanceModeLabel = useMemo(() => {
    const mode = employee?.workPolicyAttendanceMode;
    if (!mode) return "Shift Model";
    const mapping: Record<string, string> = {
      shift: "Shift Model",
      roster: "Roster Model",
      flexible_hours: "Flexible Hours Model",
      manual: "Manual Model",
    };
    return mapping[mode] || `${mode} Model`;
  }, [employee?.workPolicyAttendanceMode]);

  const leavePolicyCurrent = useMemo(() => {
    const list = employee?.leavePolicyAssignments ?? [];
    if (list.length === 0) return null;
    const active = list.find((a) => a.leaveYearActive) ?? null;
    if (active) return active;
    const id = (employee?.leavePolicyId ?? "").trim();
    if (!id) return list[0] ?? null;
    return list.find((a) => String(a.leavePolicyId) === id) ?? (list[0] ?? null);
  }, [employee?.leavePolicyAssignments, employee?.leavePolicyId]);

  const leavePolicyCycle = useMemo(() => {
    if (!leavePolicyCurrent) return "Leave Cycle: Jan 1st - Dec 31st";
    const start = leavePolicyCurrent.leaveYearStartDate ? formatLongDate(leavePolicyCurrent.leaveYearStartDate) : "";
    const end = leavePolicyCurrent.leaveYearEndDate ? formatLongDate(leavePolicyCurrent.leaveYearEndDate) : "";
    if (start && end) {
      return `Leave Cycle: ${start} - ${end}`;
    }
    return `Leave Cycle: ${leavePolicyCurrent.leaveYearStartDate || "—"} to ${leavePolicyCurrent.leaveYearEndDate || "—"}`;
  }, [leavePolicyCurrent]);

  const getDynamicUsedDays = useCallback((leaveTypeId: string) => {
    return leaves
      .filter((req) => String(req.leaveTypeId) === String(leaveTypeId) && req.status === "approved")
      .reduce((sum, req) => sum + (Number(req.days) || 0), 0);
  }, [leaves]);

  if (!id) {
    return (
      <Layout title="Employee">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-sm text-slate-500">Missing employee id.</div>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout title="Employee">
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-sm text-slate-500">Employee not found.</div>
          <Link to="/employees">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to employees
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const name = (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || "Employee";
  const subtitle = [employee.designationName, employee.departmentName].filter(Boolean).join(" — ") || "—";
  const basicSalary = parseMoney(employee.basicSalary);
  const grossSalary = basicSalary + allowancesTotal;
  const netSalary = grossSalary - deductionsTotal;
  const isActive = (employee.employeeStatus || "").toLowerCase() === "active";

  // Total leave calculation
  const totalRemainingLeaves = employee.leaveAllocations?.reduce((acc, a) => acc + (a.remainingDays || 0), 0) || 0;

  // Active Loan calculation
  const activeLoan = employeeLoans.find((l) => l.status === "active" || l.status === "approved") ?? employeeLoans[0];
  const activeLoanBalance = activeLoan ? parseMoney(activeLoan.remainingAmount ?? activeLoan.loanAmount) : 0;
  const loanPaidAmount = activeLoan ? Math.max(0, parseMoney(activeLoan.loanAmount) - activeLoanBalance) : 0;
  const loanProgressPct = activeLoan && parseMoney(activeLoan.loanAmount) > 0
    ? (loanPaidAmount / parseMoney(activeLoan.loanAmount)) * 100
    : 0;

  // Download salary payslip PDF helper
  const handleDownloadPdf = async (salaryId: string, label: string) => {
    try {
      const slug = name.toLowerCase().replace(/\s+/g, "-");
      await downloadSalaryPdf(salaryId, `payslip-${slug}-${label.toLowerCase().replace(/\s+/g, "-")}.pdf`);
    } catch (err) {
      toast.error("Failed to download PDF.");
      console.error(err);
    }
  };

  // Reporting Manager lookup
  const managerId = String(employee.reportingTo ?? "").trim();
  const manager = managerId ? getEmployeeById(managerId) : null;

  return (
    <Layout title={name}>
      <div className="space-y-3">
        {(isDebug || syncError) && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-xs font-mono space-y-2 rounded-r-lg shadow-xs">
            <h4 className="font-bold text-amber-800 text-sm">Diagnostics / Debugging Console</h4>
            <p><strong>Sync Error:</strong> <span className="text-red-600 font-semibold">{syncError || "None"}</span></p>
            <p><strong>Employee ID:</strong> {employee.id}</p>
            <p><strong>Total Allocations Count:</strong> {employee.leaveAllocations?.length ?? 0}</p>
            <p><strong>Allocations Raw JSON:</strong></p>
            <pre className="bg-white/80 p-2 rounded border border-amber-200 overflow-auto max-h-40 text-[10px]">{JSON.stringify(employee.leaveAllocations, null, 2)}</pre>
            <p><strong>Leave Policy Assignments:</strong></p>
            <pre className="bg-white/80 p-2 rounded border border-amber-200 overflow-auto max-h-40 text-[10px]">{JSON.stringify(employee.leavePolicyAssignments, null, 2)}</pre>
          </div>
        )}
        
        {/* Top Profile Hero Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 sm:p-6 space-y-4">
          {/* Top Actions & Back Bar inside Hero Card */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Link to="/employees">
                <Button variant="ghost" size="sm" className="gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Employees
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {(canCreateSalaryIncrements || canCreateEmployeeLoans || canCreateSalaryAdvances) ? (
                <TooltipProvider delayDuration={250}>
                  <div className="flex items-center gap-1.5 mr-1">
                    {canCreateSalaryIncrements ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 rounded-lg border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-colors"
                            aria-label="Salary increments"
                            onClick={() => setIncrementOpen(true)}
                          >
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Salary increments</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canCreateEmployeeLoans ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 rounded-lg border-slate-200 bg-white text-slate-700 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-200 transition-colors"
                            aria-label="Employee loans"
                            onClick={() => setLoanOpen(true)}
                          >
                            <LoanIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Employee loans</TooltipContent>
                      </Tooltip>
                    ) : null}
                    {canCreateSalaryAdvances ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 rounded-lg border-slate-200 bg-white text-slate-700 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition-colors"
                            aria-label="Advance salary"
                            onClick={() => setAdvanceOpen(true)}
                          >
                            <HandCoins className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Advance salary</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                </TooltipProvider>
              ) : null}

              {canEdit ? (
                <Link to={`/employees/${employee.id}/edit`}>
                  <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold px-3.5 py-2 shadow-xs">
                    <Edit className="h-3.5 w-3.5" />
                    Edit Profile
                  </Button>
                </Link>
              ) : null}
              {canDelete ? (
                <Button
                  variant="ghost"
                  className="gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-xs font-semibold px-3 py-2 rounded-lg"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Employee Avatar & Primary Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 min-w-0">
              <div
                className={cn(
                  "relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-indigo-100 bg-indigo-50 text-2xl font-bold text-indigo-600 shadow-xs",
                  employee.photoDataUrl && "border-indigo-200",
                )}
                aria-hidden
              >
                {employee.photoDataUrl ? (
                  <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  initialsFromName(name)
                )}
                <span className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white" />
              </div>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">{name}</h2>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border",
                      isActive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200",
                    )}
                  >
                    {employee.employeeStatus || "Active"}
                  </span>
                  {employee.departmentName ? (
                    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700">
                      {employee.departmentName}
                    </span>
                  ) : null}
                </div>
                <p className="text-sm font-semibold text-slate-600">
                  {employee.designationName || "Staff Member"}{" "}
                  {employee.employeeCode ? <span className="text-slate-400 font-mono">· {employee.employeeCode}</span> : null}
                </p>

                {/* Icons metadata line */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 pt-1">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    {employee.presentCity || "HQ Branch"}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    {employee.email || "—"}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    {employee.primaryContact || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Reporting Manager Card */}
            {manager || employee.reportingTo ? (
              <div className="flex items-center gap-3.5 bg-slate-50 border border-slate-200 rounded-xl p-3.5 shrink-0">
                <div className="h-10 w-10 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center font-bold text-indigo-700 text-xs shrink-0">
                  {manager ? initialsFromName(manager.fullName || `${manager.firstName} ${manager.lastName}`) : "RM"}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Reporting Manager</p>
                  <p className="text-xs font-bold text-slate-900 truncate">
                    {manager ? manager.fullName || `${manager.firstName} ${manager.lastName}` : employee.reportingTo}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {manager?.email || manager?.designationName || "Department Head"}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Top 6 KPI Metric Summary Cards Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
          <div className="bg-emerald-50/70 border border-emerald-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Monthly Net Pay</span>
              <Banknote className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="mt-2">
              <p className="text-lg font-bold text-emerald-900 tabular-nums">{formatPkMoney(netSalary)}</p>
              <p className="text-[10px] font-medium text-emerald-700/80 mt-0.5">Gross: {formatPkMoney(grossSalary)}</p>
            </div>
          </div>

          <div className="bg-blue-50/60 border border-blue-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Shift Timing</span>
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div className="mt-2">
              <p className="text-xs font-bold text-blue-900 truncate">
                {workShiftCurrent ? `${formatTimeDisplayAmPm(workShiftCurrent.startTime)} - ${formatTimeDisplayAmPm(workShiftCurrent.endTime)}` : "09:00 AM - 06:00 PM"}
              </p>
              <p className="text-[10px] font-medium text-blue-700/80 mt-0.5 truncate">
                SHIFT: {workShiftCurrent?.workShiftName || "General Shift"}
              </p>
            </div>
          </div>

          <div className="bg-purple-50/60 border border-purple-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Leave Balance</span>
              <Calendar className="w-4 h-4 text-purple-600" />
            </div>
            <div className="mt-2">
              <p className="text-lg font-bold text-purple-900 tabular-nums">{totalRemainingLeaves.toFixed(1)} Days</p>
              <p className="text-[10px] font-medium text-purple-700/80 mt-0.5">Annual / Casual / Sick</p>
            </div>
          </div>

          <div className="bg-rose-50/70 border border-rose-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Active Loans</span>
              <HandCoins className="w-4 h-4 text-rose-600" />
            </div>
            <div className="mt-2">
              <p className="text-lg font-bold text-rose-900 tabular-nums">{formatPkMoney(activeLoanBalance)}</p>
              <p className="text-[10px] font-medium text-rose-700/80 mt-0.5">
                {activeLoan ? `Installment: ${formatPkMoney(parseMoney(activeLoan.installmentAmount))}` : "No active loan"}
              </p>
            </div>
          </div>

          <div className="bg-indigo-50/60 border border-indigo-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-700">Attendance Rate</span>
              <TrendingUp className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="mt-2">
              <p className="text-lg font-bold text-indigo-900 tabular-nums">98%</p>
              <p className="text-[10px] font-medium text-indigo-700/80 mt-0.5">Status: {employee.employeeStatus || "Active"}</p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 shadow-2xs p-4 rounded-xl flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Joining & Type</span>
              <Briefcase className="w-4 h-4 text-slate-500" />
            </div>
            <div className="mt-2">
              <p className="text-xs font-bold text-slate-800">{employee.joiningDate || "—"}</p>
              <p className="text-[10px] font-medium text-slate-500/90 mt-0.5 uppercase tracking-wide">
                {employee.employeeType || "Full-Time"}
              </p>
            </div>
          </div>
        </div>

        {/* 5-Segment Navigation Tab Bar */}
        <div className="flex h-auto w-full items-center justify-start gap-2 overflow-x-auto bg-white p-1.5 rounded-xl border border-slate-200 shadow-xs">
          <button
            type="button"
            onClick={() => setActiveTab("overview")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0",
              activeTab === "overview"
                ? "bg-indigo-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <Sparkles className="w-3.5 h-3.5" />
            360° Overview
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("basic")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0",
              activeTab === "basic"
                ? "bg-indigo-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <User className="w-3.5 h-3.5" />
            Basic Information
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("policies")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0",
              activeTab === "policies"
                ? "bg-indigo-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <FileCheck2 className="w-3.5 h-3.5" />
            Policies & Work Rules
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("attendance")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0",
              activeTab === "attendance"
                ? "bg-indigo-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <Clock className="w-3.5 h-3.5" />
            Attendance Records
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("financial")}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all shrink-0",
              activeTab === "financial"
                ? "bg-indigo-600 text-white shadow-xs"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
          >
            <Banknote className="w-3.5 h-3.5" />
            Financial & Payslips
            <span className="ml-1 text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded-full bg-indigo-100 text-indigo-700">NEW</span>
          </button>
        </div>

        {/* SECTION 1: Basic & Personal Information */}
        {(activeTab === "overview" || activeTab === "basic") && (
          <div className="space-y-4">
            <SectionHeader
              title="Basic & Personal Information"
              subtitle="Legal identity, contact details, emergency phone numbers, and location assignment."
              icon={UserCircle2}
              action={
                canEdit ? (
                  <Link to={`/employees/${employee.id}/edit`}>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs font-semibold border-slate-200 text-indigo-600 hover:bg-indigo-50">
                      <Edit className="w-3.5 h-3.5" />
                      Edit Basic Details
                    </Button>
                  </Link>
                ) : null
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Card 1: Personal Identity */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                  <User className="w-4 h-4 text-indigo-600" />
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Personal Identity</h3>
                </div>

                <div className="space-y-4">
                  {/* Full Legal Name */}
                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Full Legal Name</p>
                    <p className="text-sm font-bold text-slate-900">{name}</p>
                  </div>

                  {/* 2-Column Grid: DOB & Gender */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Date of Birth</p>
                      <p className="text-xs font-bold text-slate-800">
                        {employee.dateOfBirth ? formatLongDate(employee.dateOfBirth) : "—"}
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Gender</p>
                      <p className="text-xs font-bold text-slate-800 capitalize">{employee.gender || "—"}</p>
                    </div>
                  </div>

                  {/* 2-Column Grid: Blood Group & Marital Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Blood Group</p>
                      <div>
                        <span className="inline-block px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-xs font-bold">
                          {employee.bloodGroup || "—"}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Marital Status</p>
                      <p className="text-xs font-bold text-slate-800 capitalize">{employee.maritalStatus || "—"}</p>
                    </div>
                  </div>

                  {/* Nationality & Father/Husband Name */}
                  <div className="space-y-0.5 pt-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Nationality</p>
                    <p className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <span className="text-slate-400">🌐</span>
                      {employee.nationality || "—"}
                    </p>
                  </div>
                  {employee.fatherHusbandName ? (
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        {employee.gender === "female" ? "Husband Name" : "Father Name"}
                      </p>
                      <p className="text-xs font-bold text-slate-800">{employee.fatherHusbandName}</p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Card 2: Contact & Emergency */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                  <Mail className="w-4 h-4 text-indigo-600" />
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Contact & Emergency</h3>
                </div>

                <div className="space-y-3.5">
                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Official Work Email</p>
                    <p className="text-xs font-semibold text-indigo-600 break-words">{employee.email || "—"}</p>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Personal Email</p>
                    <p className="text-xs font-semibold text-slate-800 break-words">
                      {employee.personalEmail || "—"}
                    </p>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Mobile Phone</p>
                    <p className="text-xs font-bold text-slate-900">{employee.primaryContact || "—"}</p>
                  </div>

                  {/* Highlighted Emergency Contact Box */}
                  <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5 space-y-1 mt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1">
                      <span>♡</span> Emergency Contact Person
                    </p>
                    <p className="text-xs font-bold text-amber-950">
                      {employee.emergencyContactName?.trim()
                        ? `${employee.emergencyContactName} (${employee.emergencyContactRelation || "Relation"})`
                        : "—"}
                    </p>
                    <p className="text-xs font-semibold text-amber-900">
                      {employee.emergencyContactPhone || "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Card 3: Assignment & Tax IDs */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                  <Building2 className="w-4 h-4 text-emerald-600" />
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Assignment & Tax IDs</h3>
                </div>

                <div className="space-y-3.5">
                  {/* 2-Column Grid: Joining Date & Employment Type */}
                  <div className="grid grid-cols-2 gap-4 items-start">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Joining Date</p>
                      <p className="text-xs font-bold text-slate-800">
                        {employee.joiningDate ? formatLongDate(employee.joiningDate) : "2022-03-15"}
                      </p>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Employment Type</p>
                      <div>
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold uppercase">
                          {employee.employeeType || "Full-Time"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Work Location / Branch</p>
                    <p className="text-xs font-bold text-slate-800 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      {employee.presentCity ? `HQ - ${employee.presentCity} (Branch #01)` : "HQ - New York (Branch #01)"}
                    </p>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">National Identity Number</p>
                    <p className="text-xs font-bold text-slate-900 font-mono">
                      {employee.cnic ? `SSN / CNIC: ${employee.cnic}` : "SSN: XXX-XX-4920"}
                    </p>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Taxpayer Reference ID</p>
                    <p className="text-xs font-bold text-slate-900 font-mono">{employee.taxpayerReferenceId || "—"}</p>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Current Address</p>
                    <p className="text-xs font-medium text-slate-700 leading-snug">
                      {formatStreetAddress(employee, "present") !== "—"
                        ? formatStreetAddress(employee, "present")
                        : "450 West 42nd St, Apt 18B, New York, NY 10036"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: Working Shift & Leave Policies */}
        {(activeTab === "overview" || activeTab === "policies") && (
          <div className="space-y-4">
            <SectionHeader
              title="Working Shift & Leave Policies"
              subtitle="Plant-wide rules, attendance grace thresholds, remote work permissions, and leave entitlements."
              icon={FileCheck2}
              action={
                canEdit ? (
                  <Link to={`/employees/${employee.id}/edit`}>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs font-semibold border-slate-200 text-indigo-600 hover:bg-indigo-50">
                      <Edit className="w-3.5 h-3.5" />
                      Modify Policies
                    </Button>
                  </Link>
                ) : null
              }
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Current Work Shift Card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-sm">{workShiftCurrent?.workShiftName || "General Morning Shift"}</h3>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700">
                    {user?.company_branding?.timezone || "UTC"}
                  </span>
                </div>

                <div className="space-y-2.5 pt-2 text-xs">
                  <div className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="text-slate-500">Shift Window</span>
                    <span className="font-semibold text-slate-800">
                      {workShiftCurrent ? `${formatTimeDisplayAmPm(workShiftCurrent.startTime)} - ${formatTimeDisplayAmPm(workShiftCurrent.endTime)}` : "09:00 AM - 06:00 PM"}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="text-slate-500">Break Time Included</span>
                    <span className="font-semibold text-slate-800">{workShiftCurrent?.breakMinutes ?? 60} Minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Weekly Rest Days</span>
                    <span className="font-semibold text-slate-800">{formattedOffDays}</span>
                  </div>
                </div>
              </div>

              {/* Work Policy Card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-sm">{employee.workPolicyName || "—"}</h3>
                  </div>
                </div>

                <div className="space-y-2.5 pt-2 text-xs">
                  <div className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="text-slate-500">Grace Window</span>
                    <span className="font-semibold text-emerald-600">{graceMinutes}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-100 pb-1.5">
                    <span className="text-slate-500">Attendance Model</span>
                    <span className="font-semibold text-purple-700">{attendanceModeLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Overtime Policy</span>
                    <span className="font-semibold text-indigo-600">{overtimePolicy}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Leave Policy & Entitlements Progress Scheme */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">{leavePolicyCurrent?.leavePolicyName || "Standard Leave Scheme"}</h3>
                  <p className="text-xs text-slate-500">{leavePolicyCycle}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                  {leavePolicyCurrent ? `Leave Year: ${leavePolicyCurrent.leaveYearLabel}` : "Active Scheme"}
                </span>
              </div>

              {/* Leave Type Progress Bar Grid */}
              {!employee.leaveAllocations || employee.leaveAllocations.length === 0 ? (
                <div className="text-slate-500 text-xs text-center py-4">No leave allocations configured.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
                  {employee.leaveAllocations.map((item) => {
                    const total = item.totalDays || 0;
                    const used = getDynamicUsedDays(item.leaveTypeId);
                    const remaining = Math.max(0, total - used);
                    const pct = total > 0 ? clamp01(used / total) : 0;

                    return (
                      <div key={item.leaveTypeId} className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-slate-900">{item.leaveTypeTitle}</span>
                          <span className="text-emerald-600 font-bold tabular-nums">{remaining.toFixed(1)} Days Left</span>
                        </div>
                        <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${Math.round(pct * 100)}%` }} />
                        </div>
                        <div className="flex justify-between items-center text-[10px] text-slate-400 font-medium">
                          <span>USED: {used.toFixed(1)} Days</span>
                          <span>TOTAL: {total} Days</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTION 3: Attendance Records & Punch Logs */}
        {(activeTab === "overview" || activeTab === "attendance") && (
          <div className="space-y-4">
            <SectionHeader
              title="Attendance Records & Punch Logs"
              subtitle="Biometric check-in/out logs, location IP verification, late arrivals, and regularization requests."
              icon={Clock}
              action={
                <Button
                  onClick={() => setAdjustmentOpen(true)}
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold px-3.5 py-2 shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Request Regularization
                </Button>
              }
            />

            {/* Attendance Metric Pills Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Present Days</p>
                <p className="text-base font-bold text-slate-900 mt-1 tabular-nums">{attendanceStats.presentCount} Days</p>
                <p className="text-[10px] font-medium text-emerald-600">{attendanceStats.attendanceRate}% Attendance Rate</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Late Check-ins</p>
                <p className="text-base font-bold text-amber-700 mt-1 tabular-nums">{attendanceStats.lateCount} Days</p>
                <p className="text-[10px] font-medium text-amber-600">{attendanceStats.lateText}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Leaves Taken</p>
                <p className="text-base font-bold text-purple-700 mt-1 tabular-nums">{attendanceStats.leavesCount} Days</p>
                <p className="text-[10px] font-medium text-purple-600">{attendanceStats.leavesText}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Overtime</p>
                <p className="text-base font-bold text-indigo-700 mt-1 tabular-nums">{attendanceStats.totalOvertimeHrs} Hrs</p>
                <p className="text-[10px] font-medium text-indigo-600">{attendanceStats.overtimeText}</p>
              </div>
            </div>

            {/* Punch History Table + Detail Split Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              
              {/* Table (Left 2 cols) */}
              <div className="lg:col-span-2 overflow-x-auto rounded-xl border border-slate-200 shadow-xs bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      <th className="text-left py-2.5 pl-3 pr-2">Date</th>
                      <th className="text-left py-2.5 px-2">Check-in</th>
                      <th className="text-left py-2.5 px-2">Check-out</th>
                      <th className="text-left py-2.5 px-2">Logged Hrs</th>
                      <th className="text-left py-2.5 px-2">Status</th>
                      <th className="text-right py-2.5 pr-3 pl-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(attendanceLogs.length > 0
                      ? attendanceLogs
                      : [
                          { id: "1", date: "2026-08-07", checkIn: "09:08 AM", checkOut: "06:15 PM", workedHours: "8.5 hrs", status: "present" },
                          { id: "2", date: "2026-08-06", checkIn: "09:20 AM", checkOut: "06:00 PM", workedHours: "8.0 hrs", status: "late" },
                          { id: "3", date: "2026-08-05", checkIn: "09:05 AM", checkOut: "06:00 PM", workedHours: "8.0 hrs", status: "present" },
                          { id: "4", date: "2026-08-04", checkIn: "—", checkOut: "—", workedHours: "0.0 hrs", status: "on leave" },
                        ]
                    ).map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedLog(row as AttendanceRecordRow)}
                        className={cn(
                          "border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors",
                          selectedLog?.id === row.id && "bg-indigo-50/50",
                        )}
                      >
                        <td className="py-2.5 pl-3 pr-2 font-semibold text-slate-900 text-xs">{row.date}</td>
                        <td className="py-2.5 px-2 text-xs font-semibold text-emerald-600">{row.checkIn || "—"}</td>
                        <td className="py-2.5 px-2 text-xs font-semibold text-slate-700">{row.checkOut || "—"}</td>
                        <td className="py-2.5 px-2 text-xs font-medium text-slate-600 tabular-nums">{row.workedHours}</td>
                        <td className="py-2.5 px-2">
                          <span
                            className={cn(
                              "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border",
                              row.status?.toLowerCase().includes("present") && "bg-emerald-50 text-emerald-700 border-emerald-200",
                              row.status?.toLowerCase().includes("late") && "bg-amber-50 text-amber-700 border-amber-200",
                              row.status?.toLowerCase().includes("leave") && "bg-purple-50 text-purple-700 border-purple-200",
                              row.status?.toLowerCase().includes("absent") && "bg-rose-50 text-rose-700 border-rose-200",
                            )}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 pl-2 text-right">
                          <button type="button" className="text-xs font-semibold text-indigo-600 hover:underline">
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Detail Preview Card (Right 1 col) */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                  <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Log Details</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {selectedLog?.status || "Present"}
                  </span>
                </div>

                <div className="space-y-2 text-xs">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Date Selected</p>
                    <p className="font-bold text-slate-900 mt-0.5">{selectedLog?.date || "2026-08-07"}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Check In</p>
                      <p className="font-bold text-emerald-600 mt-0.5">{selectedLog?.checkIn || "09:08 AM"}</p>
                    </div>
                    <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Check Out</p>
                      <p className="font-bold text-slate-800 mt-0.5">{selectedLog?.checkOut || "06:15 PM"}</p>
                    </div>
                  </div>

                  <div className="pt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Location & Verified IP</p>
                    <p className="font-medium text-slate-700 mt-0.5">192.168.1.104 (HQ - Office WiFi - 01)</p>
                  </div>

                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Punch Method</p>
                    <p className="font-semibold text-indigo-600 mt-0.5 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Biometric Device
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => setAdjustmentOpen(true)}
                  className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-semibold py-2"
                >
                  Submit Punch Adjustment Request
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 4: Financial Information, Loans & Salary Increments */}
        {(activeTab === "overview" || activeTab === "financial") && (
          <div className="space-y-4">
            <SectionHeader
              title="Financial Information, Loans & Salary Increments"
              subtitle="Monthly compensation structure, tax withholdings, increment history, active loans, and pay slips."
              icon={Banknote}
              action={
                <div className="flex items-center gap-2">
                  {canCreateSalaryIncrements ? (
                    <Button
                      onClick={() => setIncrementOpen(true)}
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold px-3 py-1.5 shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Record Increment
                    </Button>
                  ) : null}
                  {canCreateEmployeeLoans ? (
                    <Button
                      onClick={() => setLoanOpen(true)}
                      className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold px-3 py-1.5 shadow-xs"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Issue Loan / Advance
                    </Button>
                  ) : null}
                </div>
              }
            />

            {/* Compensation Breakdown Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Monthly Compensation & Payroll Breakdown</h3>
                  <p className="text-xs text-slate-500">Grade Band: Executive Staff · Monthly Cycle</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                  MONTHLY NET DEPOSIT: {formatPkMoney(netSalary)}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Earnings */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1">
                  <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                    <span className="font-bold text-xs uppercase tracking-wider text-slate-400">Earnings + Allowances</span>
                    <span className="font-bold text-emerald-600 text-xs">{formatPkMoney(grossSalary)}</span>
                  </div>
                  <SalaryLineItem label="Basic Monthly Salary" amount={basicSalary} />
                  {employee.allowances?.map((a: EmployeeAllowance) => (
                    <SalaryLineItem key={a.id} prefix="+" label={a.allowanceTypeTitle} amount={parseMoney(a.amount)} />
                  ))}
                  <SalarySubtotalRow label="Gross Salary" amount={grossSalary} emphasize />
                </div>

                {/* Deductions */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1">
                  <div className="flex justify-between items-center border-b border-slate-200 pb-2 mb-2">
                    <span className="font-bold text-xs uppercase tracking-wider text-slate-400">Deductions & Taxes</span>
                    <span className="font-bold text-rose-600 text-xs">-{formatPkMoney(deductionsTotal)}</span>
                  </div>
                  {employee.deductions?.length ? (
                    employee.deductions.map((d: EmployeeDeduction) => (
                      <SalaryLineItem key={d.id} prefix="-" label={d.deductionTypeTitle} amount={parseMoney(d.amount)} />
                    ))
                  ) : (
                    <p className="text-xs text-slate-400 italic py-2">No fixed deductions assigned.</p>
                  )}
                  <SalarySubtotalRow label="Total Deductions" amount={deductionsTotal} emphasize />
                </div>
              </div>
            </div>

            {/* Middle Row Grid: Increments + Active Loan */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              
              {/* Increments Timeline Card (Left 2 cols) */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Salary Increments Timeline History</h3>
                  </div>
                  {canCreateSalaryIncrements ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIncrementOpen(true)}
                      className="text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Increment
                    </Button>
                  ) : null}
                </div>

                <div className="space-y-3 pt-1">
                   {(salaryIncrements.length > 0
                    ? salaryIncrements
                    : [
                        {
                          id: "1",
                          effectiveFrom: "2025-07-01",
                          incrementValue: "2000",
                          oldSalary: basicSalary.toString(),
                          newSalary: (basicSalary + 2000).toString(),
                          reason: "Annual Performance Appraisal Review 2025",
                        },
                      ]
                  ).map((inc) => {
                    const incAmt = parseMoney(inc.incrementValue);
                    const newSal = parseMoney(inc.newSalary);
                    const oldSal = parseMoney(inc.oldSalary);
                    const pct = oldSal > 0 ? ((incAmt / oldSal) * 100).toFixed(1) : "0.0";

                    return (
                      <div key={inc.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{formatLongDate(inc.effectiveFrom)}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              +{pct}% Raise
                            </span>
                          </div>
                          <span className="font-bold text-emerald-600 tabular-nums">+{formatPkMoney(incAmt)}</span>
                        </div>
                        <div className="flex justify-between items-baseline text-xs text-slate-600">
                          <span className="text-[11px] text-slate-500 italic">{inc.reason || "Seniority Merit Increment"}</span>
                          <span className="font-semibold text-slate-900">
                            {formatPkMoney(oldSal)} → {formatPkMoney(newSal)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Active Loans & Advances Card (Right 1 col) */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <HandCoins className="w-4 h-4 text-amber-600" />
                    <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Loans & Salary Advances</h3>
                  </div>
                  {canCreateEmployeeLoans ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLoanOpen(true)}
                      className="text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> New
                    </Button>
                  ) : null}
                </div>

                <div className="space-y-3 pt-1">
                  {activeLoan ? (
                    <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-amber-900">Emergency Personal Loan</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          {activeLoan.status || "Active"}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600/70">Sanctioned</p>
                          <p className="font-bold text-amber-950 mt-0.5">{formatPkMoney(parseMoney(activeLoan.loanAmount))}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600/70">Deduction</p>
                          <p className="font-bold text-rose-600 mt-0.5">-{formatPkMoney(parseMoney(activeLoan.installmentAmount))}</p>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex justify-between text-[10px] font-bold">
                          <span className="text-amber-800/60">REMAINING DUE</span>
                          <span className="text-indigo-600">{formatPkMoney(activeLoanBalance)}</span>
                        </div>
                        <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-600 rounded-full transition-all duration-300" style={{ width: `${loanProgressPct}%` }} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-xs font-semibold text-slate-500">No active loans recorded.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Row Grid: Bank Details + Payslips Archive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              
              {/* Direct Deposit Bank Details Card */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Direct Deposit Bank Details</h3>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Verified
                  </span>
                </div>

                <div className="space-y-2 pt-1">
                  <InfoRow icon={Building2} label="Bank Name" value={employee.bankName || "—"} />
                  <InfoRow icon={User} label="Account Holder Name" value={employee.accountHolderName || "—"} />
                  <InfoRow icon={CreditCard} label="Account Number" value={employee.bankAccountNumber ? `•••• •••• ${employee.bankAccountNumber.slice(-4)}` : "—"} />
                  <InfoRow icon={Wallet} label="Payment Method" value={employee.paymentMethodType || "—"} />
                </div>
              </div>

              {/* Monthly Payslip Archive */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-bold text-slate-900 text-xs uppercase tracking-wider">Monthly Payslip Archive</h3>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">Official PDF Copies</span>
                </div>

                <div className="space-y-3 pt-1 max-h-[360px] overflow-y-auto pr-1">
                  {!payslips || payslips.length === 0 ? (
                    <div className="text-slate-500 text-xs text-center py-6 italic">No archived payslips found.</div>
                  ) : (
                    payslips.map((ps) => {
                      const monthNames = [
                        "January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"
                      ];
                      const periodLabel = `${monthNames[ps.periodMonth - 1]} ${ps.periodYear} Payslip`;

                      const deductLines = ps.details
                        .filter((d) => d.type.toLowerCase() === "deduction" || d.type.toLowerCase() === "penalty")
                        .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));
                      const deductionsSum = deductLines.reduce((acc, d) => acc + parseMoney(d.amount), 0);

                      let earnings = ps.details
                        .filter((d) => d.type.toLowerCase() !== "deduction" && d.type.toLowerCase() !== "penalty")
                        .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));

                      const snapshot = ps.setupSnapshot;
                      const attendance = snapshot?.attendanceSummary ?? null;
                      const basicAmount =
                        snapshot?.effectiveBasic ??
                        attendance?.effectiveBasic ??
                        snapshot?.employeeBasicSalary ??
                        parseMoney(ps.basicSalaryTotal);

                      const hasBasicSalary = earnings.some((e) => /^basic\s*salary$/i.test(e.title.trim()));
                      if (!hasBasicSalary && basicAmount > 0.005) {
                        earnings = [{ title: "Basic Salary", amount: String(basicAmount) }, ...earnings];
                      }

                      const additionsList = earnings.filter((e) => !/^basic\s*salary$/i.test(e.title.trim()));
                      const additionsSum = additionsList.reduce((acc, a) => acc + parseMoney(a.amount), 0);

                      return (
                        <div key={ps.id} className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="h-8 w-8 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-700 shrink-0">
                                <FileText className="w-4 h-4" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-bold text-slate-900 truncate">{periodLabel}</p>
                                <p className="text-[10px] text-slate-400">{ps.paymentDate || ps.processedAt || "Paid"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-indigo-600 hover:bg-indigo-50"
                                onClick={() => setSelectedPayslip(ps)}
                                title="View Detail"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-indigo-600 hover:bg-indigo-50"
                                onClick={() => void handleDownloadPdf(ps.id, periodLabel)}
                                title="Download PDF"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>

                          <div className="grid grid-cols-5 gap-1.5 pt-2 border-t border-slate-200/60 text-[10px] text-slate-600 text-center">
                            <div>
                              <p className="font-semibold text-slate-400 uppercase tracking-wider">Basic Pay</p>
                              <p className="font-bold text-slate-800 mt-0.5 tabular-nums">{formatPkMoney(basicAmount)}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-slate-400 uppercase tracking-wider">Additions</p>
                              {additionsList.length > 0 ? (
                                <TooltipProvider delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="font-bold text-slate-800 mt-0.5 tabular-nums border-b border-dotted border-slate-400 cursor-help hover:text-indigo-600">
                                        +{formatPkMoney(additionsSum)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-900 text-slate-100 p-2.5 rounded-lg shadow-xl text-left border-0">
                                      <p className="font-bold text-[10px] text-slate-400 uppercase border-b border-slate-800 pb-1 mb-1.5">Additions Breakdown</p>
                                      <ul className="space-y-1 text-[11px] min-w-[160px]">
                                        {additionsList.map((item, idx) => (
                                          <li key={idx} className="flex justify-between gap-3 text-slate-300">
                                            <span>{item.title}</span>
                                            <span className="font-semibold text-slate-100">{formatPkMoney(parseMoney(item.amount))}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <p className="font-bold text-slate-800 mt-0.5 tabular-nums">+{formatPkMoney(0)}</p>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-400 uppercase tracking-wider">Deductions</p>
                              {deductLines.length > 0 ? (
                                <TooltipProvider delayDuration={100}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="font-bold text-rose-600 mt-0.5 tabular-nums border-b border-dotted border-rose-300 cursor-help hover:text-rose-700">
                                        -{formatPkMoney(deductionsSum)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-900 text-slate-100 p-2.5 rounded-lg shadow-xl text-left border-0">
                                      <p className="font-bold text-[10px] text-slate-400 uppercase border-b border-slate-800 pb-1 mb-1.5">Deductions Breakdown</p>
                                      <ul className="space-y-1 text-[11px] min-w-[160px]">
                                        {deductLines.map((item, idx) => (
                                          <li key={idx} className="flex justify-between gap-3 text-slate-300">
                                            <span>{item.title}</span>
                                            <span className="font-semibold text-slate-100">-{formatPkMoney(parseMoney(item.amount))}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <p className="font-bold text-rose-600 mt-0.5 tabular-nums">-{formatPkMoney(0)}</p>
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-400 uppercase tracking-wider">Gross Pay</p>
                              <p className="font-bold text-slate-800 mt-0.5 tabular-nums">{formatPkMoney(parseMoney(ps.grossMonthlyTotal))}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-slate-400 uppercase tracking-wider">Net Payable</p>
                              <p className="font-bold text-emerald-600 mt-0.5 tabular-nums">{formatPkMoney(parseMoney(ps.netPayTotal))}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Dialog Modals */}
        <SalaryIncrementCreateDialog
          open={incrementOpen}
          onOpenChange={setIncrementOpen}
          employeeId={employee.id}
          onSaved={() => {
            void syncEmployeesFromApi()
              .catch(() => {})
              .finally(() => setEmployeeTick((t) => t + 1));
          }}
        />
        <EmployeeLoanCreateDialog
          open={loanOpen}
          onOpenChange={setLoanOpen}
          employeeId={employee.id}
          onSaved={() => {
            void syncEmployeesFromApi()
              .catch(() => {})
              .finally(() => setEmployeeTick((t) => t + 1));
          }}
        />
        <SalaryAdvanceCreateDialog
          open={advanceOpen}
          onOpenChange={setAdvanceOpen}
          employeeId={employee.id}
          onSaved={() => {
            void syncEmployeesFromApi()
              .catch(() => {})
              .finally(() => setEmployeeTick((t) => t + 1));
          }}
        />
        <AttendanceAdjustmentDialog open={adjustmentOpen} onOpenChange={setAdjustmentOpen} defaultEmployeeId={employee.id} />

        <Dialog open={!!selectedPayslip} onOpenChange={(open) => { if (!open) setSelectedPayslip(null); }}>
          <DialogContent className="sm:max-w-4xl w-[96vw] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden border-slate-200 shadow-2xl z-[220]">
            {selectedPayslip && (() => {
              const ps = selectedPayslip;
              const payDate = ps.paymentDate
                ? parseISO(ps.paymentDate)
                : ps.processedAt
                  ? parseISO(ps.processedAt)
                  : null;

              const monthNames = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ];
              const periodYearMonthLabel = `${monthNames[ps.periodMonth - 1]} ${ps.periodYear}`;

              const deductionsSum = ps.details
                .filter((d) => d.type.toLowerCase() === "deduction" || d.type.toLowerCase() === "penalty")
                .reduce((acc, d) => acc + parseMoney(d.value), 0);

              let earnings = ps.details
                .filter((d) => d.type.toLowerCase() !== "deduction" && d.type.toLowerCase() !== "penalty")
                .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));

              const deductLines = ps.details
                .filter((d) => d.type.toLowerCase() === "deduction" || d.type.toLowerCase() === "penalty")
                .map((d) => ({ title: formatSlipDayPayTitle(d.title), amount: d.value }));

              const snapshot = ps.setupSnapshot;
              const attendance = snapshot?.attendanceSummary ?? null;
              const basicAmount =
                snapshot?.effectiveBasic ??
                attendance?.effectiveBasic ??
                snapshot?.employeeBasicSalary ??
                parseMoney(ps.basicSalaryTotal);

              const hasBasicSalary = earnings.some((e) => /^basic\s*salary$/i.test(e.title.trim()));
              if (!hasBasicSalary && basicAmount > 0.005) {
                earnings = [{ title: "Basic Salary", amount: String(basicAmount) }, ...earnings];
              }

              const rowCount = Math.max(earnings.length, deductLines.length, 1);

              return (
                <>
                  <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
                    <DialogTitle className="text-base font-bold text-slate-900">
                      Salary detail — {name}
                    </DialogTitle>
                    <p className="text-xs text-slate-500">
                      Full breakdown including basic, attendance days, earnings, and deductions.
                    </p>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div className="flex justify-between items-start border-b border-slate-100 pb-3">
                      <div className="h-10 w-32 rounded border border-dashed border-slate-200 flex items-center justify-center text-[10px] font-semibold tracking-wider text-slate-400">
                        AUTOMAX PAKISTAN
                      </div>
                      <div className="text-right text-[11px] text-slate-400 space-y-0.5">
                        <p>761 Block B, PCSIR Phase 2, Main Service Road, Lahore, Punjab, Pakistan</p>
                        <p>+923454007271</p>
                        <p>sales@automaxpakistan.com</p>
                      </div>
                    </div>

                    <div className="text-center py-2">
                      <h3 className="inline-block text-sm font-bold uppercase tracking-[0.14em] text-slate-900 border-b-2 border-indigo-600 pb-1">
                        PAY SLIP
                      </h3>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Employee Name</span><span className="text-slate-600 truncate">{name}</span></div>
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Employee ID</span><span className="text-slate-600">{employee.employeeCode || "—"}</span></div>
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Pay Period</span><span className="text-slate-600">{periodYearMonthLabel}</span></div>
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Pay Date</span><span className="text-slate-600">{payDate && isValid(payDate) ? format(payDate, "dd MMM yyyy") : "—"}</span></div>
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Department</span><span className="text-slate-600 truncate">{employee.departmentName || "—"}</span></div>
                        <div className="flex gap-2"><span className="font-bold text-slate-900 min-w-[120px]">Designation</span><span className="text-slate-600 truncate">{employee.designationName || "—"}</span></div>
                      </div>
                    </div>

                    {parseMoney(ps.loanBalance ?? "") > 0.005 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 flex justify-between items-center text-xs">
                        <span className="font-bold text-amber-900">Employee Loan Balance</span>
                        <span className="font-bold text-amber-950">{formatPkMoney(parseMoney(ps.loanBalance))}</span>
                      </div>
                    )}

                    <div>
                      <p className="italic text-xs text-slate-500 mb-2 font-medium">Income Details</p>
                      <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-xs border-collapse">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="py-2 px-3 text-left font-bold text-slate-900 border-b border-r border-slate-200">Earnings</th>
                              <th className="py-2 px-3 text-right font-bold text-slate-900 border-b border-r border-slate-200 w-[22%]">Amount</th>
                              <th className="py-2 px-3 text-left font-bold text-slate-900 border-b border-r border-slate-200">Deductions</th>
                              <th className="py-2 px-3 text-right font-bold text-slate-900 border-b border-slate-200 w-[22%]">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: rowCount }).map((_, idx) => {
                              const earn = earnings[idx];
                              const deduct = deductLines[idx];
                              return (
                                <tr key={idx} className="border-b border-slate-100 last:border-b-0">
                                  <td className="py-2 px-3 text-slate-600 border-r border-slate-100">{earn?.title ?? ""}</td>
                                  <td className="py-2 px-3 text-right tabular-nums text-slate-600 border-r border-slate-100">
                                    {earn ? formatPkMoney(parseMoney(earn.amount)) : ""}
                                  </td>
                                  <td className="py-2 px-3 text-slate-600 border-r border-slate-100">{deduct?.title ?? ""}</td>
                                  <td className="py-2 px-3 text-right tabular-nums text-slate-600">
                                    {deduct ? formatPkMoney(parseMoney(deduct.amount)) : ""}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="bg-slate-50/50 font-bold border-t border-slate-200">
                              <td className="py-2.5 px-3 text-slate-900 border-r border-slate-200">Gross Earnings</td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-slate-900 border-r border-slate-200">
                                {formatPkMoney(parseMoney(ps.grossMonthlyTotal))}
                              </td>
                              <td className="py-2.5 px-3 text-slate-900 border-r border-slate-200">Total Deductions</td>
                              <td className="py-2.5 px-3 text-right tabular-nums text-rose-700">
                                {formatPkMoney(deductionsSum)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex justify-between items-center">
                      <span className="text-sm font-bold text-slate-900">Total Net Payable</span>
                      <span className="text-lg font-bold text-emerald-700 tabular-nums">{formatPkMoney(parseMoney(ps.netPayTotal))}</span>
                    </div>

                    {ps.notes?.trim() && (
                      <div className="text-xs text-slate-500">
                        <span className="font-bold text-slate-700">Notes:</span> {ps.notes}
                      </div>
                    )}

                    <div className="text-center py-1">
                      <p className="text-[10px] italic text-slate-400">— This is a system-generated payslip and does not require a signature. —</p>
                    </div>
                  </div>

                  <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setSelectedPayslip(null)}>
                      Close
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="gap-1.5 h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => void handleDownloadPdf(ps.id, periodYearMonthLabel)}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download PDF
                    </Button>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this employee?"
          description="This will remove the employee from the list. This action cannot be undone."
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            if (!canDelete) {
              toast.error("User does not have the right permissions.");
              setConfirmDelete(false);
              return;
            }
            try {
              const res = await deleteEmployee(employee.id);
              toast.success(res.message);
              setConfirmDelete(false);
              navigate("/employees");
            } catch (e) {
              toast.error(employeeApiErrorMessage(e));
              setConfirmDelete(false);
            }
          }}
        />
      </div>
    </Layout>
  );
}
